import { useMutation, useReactiveVar } from "@apollo/client";
import {
  Center,
  Collapse,
  Divider,
  Grid,
  GridItem,
  HStack,
  Icon,
  Text,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { BsExclamationTriangleFill } from "react-icons/bs";
import { Navigate, useNavigate } from "react-router-dom";
import { ComponentLoading } from "../../../components/loading/ComponentLoading";
import { CREATE_GAM_CAMPAIGN } from "../../../graphql/operations/campaignOperations/operations";
import { userDetailsVar } from "../../../lib/cache";
import { publisherMapChoice } from "../../../utils/publisherMap";
import { GamCampaignDetails } from "./GamCampaignDetails";
import { GamCampaignDomains } from "./GamCampaignDomains";
import { GamCampaignFormDataClass } from "./gamCampaignFormSchema";
import { GamCampaignIds } from "./GamCampaignIds";
import { GamCampaignOwnerEmail } from "./GamCampaignOwnerEmail";
import { SubmissionBar } from "./SubmissionBar";

export const GamCampaignForm = () => {
    const currentUser = useReactiveVar(userDetailsVar);
    const navigate = useNavigate();
    const [createGamCampaign] = useMutation(CREATE_GAM_CAMPAIGN);
    const [gamCampaignFormData, setGamCampaignFormData] = useState(
        new GamCampaignFormDataClass(currentUser?.isStaff)
    );

    useEffect(() => {
        setGamCampaignFormData(new GamCampaignFormDataClass(currentUser?.isStaff));
      }, [currentUser]);

    if (!currentUser) {
        return <ComponentLoading text={"Loading page..."} />;
    }

    if (!(currentUser?.isStaff || currentUser?.agency.partner)) {
        return <Navigate to={"/"} />;
    }

    return (
        <>
          <Formik
            initialValues={gamCampaignFormData.getDefaultValues()}
            validationSchema={gamCampaignFormData.getYupSchema()}
            onSubmit={(values, { setSubmitting, setErrors, setStatus }) => {
              createGamCampaign({
                variables: {
                  packageDetails: {
                    ...values,
                    learnFromIds: values.learnFromIds.split(","),
                    publisher: publisherMapChoice(values.publisher),
                  },
                },
              })
                .then((res) => {
                  navigate(`../${res.data.optimisationPackageCreate.uniqueId}`);
                })
                .catch((err) => {
                  err.graphQLErrors.forEach(
                    (e: {
                      message: string;
                      extensions?: Record<string, string>;
                    }) => {
                      if (e.extensions) {
                        setErrors(e.extensions);
                        setStatus("Your form contains errors.");
                      } else {
                        setStatus(e.message);
                        window.scrollTo({
                          top: 0,
                          behavior: "smooth",
                        });
                      }
                      setSubmitting(false);
                    }
                  );
                });
              setStatus("");
            }}
          >
            {(formik) => {
              return (
                <>
                  <Collapse in={!!formik.status} animateOpacity={true}>
                    <Center>
                      <HStack
                        color={"error"}
                        bg={"rgb(252, 234, 234)"}
                        p={5}
                        spacing={6}
                        mb={4}
                      >
                        <Icon as={BsExclamationTriangleFill} w={10} h={10} />
                        <Text fontSize={"16px"} fontWeight={"bold"}>
                          {formik.status}
                        </Text>
                      </HStack>
                    </Center>
                  </Collapse>
                  <Form>
                    <Grid
                      templateColumns={"repeat(2, 1fr)"}
                      gap={2}
                      rowGap={8}
                      p={10}
                      bg={"white"}
                      borderRadius={"sm"}
                      w={"100%"}
                      height={"100%"}
                      justifyContent={"center"}
                    >
                      {gamCampaignFormData.fields?.packageOwnerEmail &&
                        <>
                          <GridItem colSpan={1}>
                            <GamCampaignOwnerEmail
                              fields={gamCampaignFormData.getBasicDetails()}
                              formik={formik}
                              defaultValues={gamCampaignFormData.getDefaultValues()}
                              gamCampaignFormData={gamCampaignFormData}
                              // minHeight={"18rem"}
                              w={"100%"}
                            />
                          </GridItem>
                          <GridItem colSpan={1}/>
                        </>
                      }

                      <GamCampaignDetails
                        fields={gamCampaignFormData.getBasicDetails()}
                        formik={formik}
                        defaultValues={gamCampaignFormData.getDefaultValues()}
                        w={"100%"}
                      />

                      <GamCampaignIds
                        fields={gamCampaignFormData.getBasicDetails()}
                        formik={formik}
                        defaultValues={gamCampaignFormData.getDefaultValues()}
                        minHeight={"18rem"}
                        w={"100%"}
                      />

                      <GamCampaignDomains
                        fields={gamCampaignFormData.getBasicDetails()}
                        formik={formik}
                        defaultValues={gamCampaignFormData.getDefaultValues()}
                        w={"100%"}
                      />

                      <GridItem colSpan={2}>
                        <Divider mt={5} />
                        <SubmissionBar formik={formik}/>
                      </GridItem>
                    </Grid>
                  </Form>
                </>
              );
            }}
          </Formik>
        </>
      );
}
